import { useMemo } from "react";

import { useRequest } from "~/v2/api/hooks";
import {
  getSessionStart,
  getWatchlistLivePrices,
  getWatchlistSparklines,
  type WatchlistLivePrice,
  watchlistMarketDataQueryKeys,
} from "~/v3-poc/api/watchlistMarketData";
import type { IndexRowData } from "../types";

/**
 * Design labels want SPX / IXIC / DJI, but Alpaca market data (stock
 * snapshots + bars via `watchlistMarketData` / `DATA_API_URL`) does not serve
 * true index prints — only tradable equities/ETFs. Index options underlyings
 * exist for trading, not as a quote feed here. Keep the tracking ETFs and show
 * those symbols so price levels stay honest (~ETF not ~index).
 */
const INDICES: ReadonlyArray<{ symbol: string; name: string }> = [
  { symbol: "SPY", name: "S&P 500" },
  { symbol: "QQQ", name: "Nasdaq-100" },
  { symbol: "DIA", name: "Dow Jones" },
];

const SYMBOLS = INDICES.map((index) => index.symbol);

const LIVE_PRICE_POLLING_INTERVAL = 5_000;
const SPARKLINE_POLLING_INTERVAL = 60_000;

export interface BuildMarketIndexRowsParams {
  indices: readonly Pick<IndexRowData, "symbol" | "name">[];
  livePrices: Record<string, WatchlistLivePrice> | undefined;
  sparklines: Record<string, number[]> | undefined;
  isLivePricesLoading: boolean;
  isLivePricesError: boolean;
  isSparklinesLoading: boolean;
  isSparklinesError: boolean;
}

export const buildMarketIndexRows = ({
  indices,
  livePrices,
  sparklines,
  isLivePricesLoading,
  isLivePricesError,
  isSparklinesLoading,
  isSparklinesError,
}: BuildMarketIndexRowsParams): IndexRowData[] =>
  indices.map(({ symbol, name }) => {
    const live = livePrices?.[symbol];
    const spark = sparklines?.[symbol] ?? [];
    return {
      symbol,
      name,
      price: live?.price,
      previousClose: live?.previousClose,
      spark,
      hasError:
        !isLivePricesLoading &&
        (isLivePricesError || live?.price === undefined),
      hasSparkError:
        !isSparklinesLoading && (isSparklinesError || spark.length < 2),
    };
  });

export interface MarketIndicesCardControllerValue {
  rows: IndexRowData[];
  isLoading: boolean;
}

export const useMarketIndicesCardController =
  (): MarketIndicesCardControllerValue => {
    const {
      data: livePrices,
      isLoading: isLivePricesLoading,
      isError: isLivePricesError,
    } = useRequest(
      watchlistMarketDataQueryKeys.livePrices(SYMBOLS),
      () => getWatchlistLivePrices(SYMBOLS),
      {
        refetchInterval: LIVE_PRICE_POLLING_INTERVAL,
        staleTime: LIVE_PRICE_POLLING_INTERVAL,
        meta: { showErrorToast: false },
      },
    );

    const sessionStart = getSessionStart();
    const {
      data: sparklines,
      isLoading: isSparklinesLoading,
      isError: isSparklinesError,
    } = useRequest(
      watchlistMarketDataQueryKeys.sparklines(SYMBOLS, sessionStart),
      () => getWatchlistSparklines({ symbols: SYMBOLS, sessionStart }),
      {
        refetchInterval: SPARKLINE_POLLING_INTERVAL,
        staleTime: SPARKLINE_POLLING_INTERVAL,
        meta: { showErrorToast: false },
      },
    );

    const rows = useMemo(
      () =>
        buildMarketIndexRows({
          indices: INDICES,
          livePrices,
          sparklines,
          isLivePricesLoading,
          isLivePricesError,
          isSparklinesLoading,
          isSparklinesError,
        }),
      [
        livePrices,
        sparklines,
        isLivePricesLoading,
        isLivePricesError,
        isSparklinesLoading,
        isSparklinesError,
      ],
    );

    return { rows, isLoading: isLivePricesLoading };
  };
